/**
 * createDetailFieldHook — factory for codegen-emitted per-detail-entity
 * field hooks (e.g. `useGetMarketField`).
 *
 * Pair with a detail hook (`kind: detail`). The field hook reads the
 * SAME cache entry the parent hook writes — same args → same queryKey —
 * and runs TanStack's canonical "per-component select" pattern to slice
 * a single field. Per-leaf re-render isolation comes from the default
 * tracked-properties Proxy + structural sharing: when the cached row
 * patches but the selected field's value is `===` to before, the
 * observer never notifies.
 *
 * Usage (codegen-emitted):
 *
 *     const fields = ['title', 'volume24h'] as const;
 *     function MarketDetail({ pubkey }) {
 *       useGetMarket({ pubkey, fields });   // fetches/subscribes
 *       return (
 *         <>
 *           <Title pubkey={pubkey} />
 *           <Volume pubkey={pubkey} />
 *         </>
 *       );
 *     }
 *     function Title({ pubkey }) {
 *       const v = useGetMarketField({ pubkey, fields }, 'title');
 *       return <h3>{v}</h3>;
 *     }
 *
 * Docs alignment:
 *   - https://tanstack.com/query/latest/docs/framework/react/guides/render-optimizations
 *   - https://tanstack.com/query/latest/docs/framework/react/guides/query-options
 *       ("A very common and useful pattern is to create per-component select functions")
 */
import { hashKey, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSdkViewerScope } from '../../provider/SdkRuntimeContext.js';
import { hashDiagnosticEntity } from '../../tx-engine/diagnostics/lifecycle.js';
import { useConvictionProjectionInterest } from '../internal/conviction-projection-interest.js';
import { materializeNestedEntityProjection, nestedEntityIds, projectionEntityQueryKey, recordOutcomeEntityDiagnostic, } from '../internal/entity-projection.js';
import { refetchRealtimeQuery } from '../internal/refetchRealtimeQuery.js';
import { resolveEntityQueryKey } from '../internal/scoped-entity-store.js';
import { createSubscriberQueryFn, getSubscriberQueryMetaOptions, } from '../internal/subscriber-query-fn.js';
import { recordViewerOverlayRecovery, viewerOverlayKeyForField, viewerOverlayQueryKey, } from '../internal/viewer-overlay.js';
export function createDetailFieldHook(spec) {
    return function useDetailField(args, field) {
        const factory = spec.entityKeys[spec.kind];
        if (factory === undefined) {
            throw new Error(`[morfi-sdk] ${spec.specName}Field: spec.entityKeys.${spec.kind} is undefined ` +
                '(codegen invariant violation).');
        }
        const factoryQueryKey = factory(args).queryKey;
        const queryClient = useQueryClient();
        const viewerScope = useSdkViewerScope();
        const entityId = String(args.pubkey ?? factoryQueryKey[factoryQueryKey.length - 1] ?? '');
        useConvictionProjectionInterest(queryClient, spec.specName === 'useGetMarket' ? entityId : undefined);
        const resolvedEntityQueryKey = resolveEntityQueryKey(spec, args, viewerScope);
        const queryKey = spec.canonicalEntity === true && resolvedEntityQueryKey !== undefined && entityId.length > 0
            ? resolvedEntityQueryKey(entityId)
            : factoryQueryKey;
        const identityViewerField = spec.viewerOverlay?.identityFields.includes(field) === true;
        const walletViewerField = spec.viewerOverlay?.walletFields.includes(field) === true;
        const viewerField = identityViewerField || walletViewerField;
        const overlayQueryKey = resolvedEntityQueryKey !== undefined && spec.viewerOverlay !== undefined && viewerField
            ? (viewerOverlayKeyForField(resolvedEntityQueryKey, entityId, field, spec.viewerOverlay, args, viewerScope) ??
                viewerOverlayQueryKey(resolvedEntityQueryKey, entityId, walletViewerField ? 'wallet' : 'identity', viewerScope))
            : undefined;
        const effectiveQueryKey = overlayQueryKey ?? queryKey;
        const select = useCallback((data) => {
            if (data === undefined)
                return undefined;
            return data[field];
        }, [field]);
        // Cache-resolving subscriber queryFn — see `subscriber-query-fn.ts`
        // for why this can't be a singleton. Re-built per queryKey identity.
        const queryFn = useMemo(() => createSubscriberQueryFn(queryClient, effectiveQueryKey), [queryClient, effectiveQueryKey]);
        const { data: value } = useQuery({
            queryKey: effectiveQueryKey,
            queryFn,
            ...getSubscriberQueryMetaOptions(queryClient, queryKey),
            enabled: false,
            staleTime: Number.POSITIVE_INFINITY,
            select,
        });
        const nestedProjection = spec.nestedEntityProjection;
        const nestedIds = nestedProjection?.field === field
            ? nestedEntityIds({ [field]: value }, nestedProjection)
            : [];
        useQueries({
            queries: nestedProjection === undefined
                ? []
                : nestedIds.map((nestedId) => {
                    const nestedKey = projectionEntityQueryKey(nestedProjection, nestedId);
                    return {
                        queryKey: nestedKey,
                        queryFn: createSubscriberQueryFn(queryClient, nestedKey),
                        enabled: false,
                        staleTime: Number.POSITIVE_INFINITY,
                    };
                }),
        });
        const missingNestedIds = nestedProjection === undefined
            ? []
            : nestedIds.filter((nestedId) => queryClient.getQueryData(projectionEntityQueryKey(nestedProjection, nestedId)) ===
                undefined);
        const nestedRecoveryKey = missingNestedIds.join('|');
        const nestedRecoveryRef = useRef(null);
        useEffect(() => {
            if (nestedProjection === undefined || nestedRecoveryKey.length === 0) {
                nestedRecoveryRef.current = null;
                return;
            }
            const recoveringIds = nestedRecoveryKey.split('|');
            if (recoveringIds.every((nestedId) => queryClient.getQueryData(projectionEntityQueryKey(nestedProjection, nestedId)) !==
                undefined)) {
                nestedRecoveryRef.current = null;
                return;
            }
            if (nestedRecoveryRef.current === nestedRecoveryKey)
                return;
            nestedRecoveryRef.current = nestedRecoveryKey;
            void refetchRealtimeQuery(queryClient, queryKey).then((outcome) => {
                const stillMissing = recoveringIds.some((nestedId) => queryClient.getQueryData(projectionEntityQueryKey(nestedProjection, nestedId)) ===
                    undefined);
                if (nestedProjection.resource === 'outcomes' && (outcome !== 'success' || stillMissing)) {
                    recordOutcomeEntityDiagnostic({
                        kind: 'missing-entity-recovery-failure',
                        entityHash: hashDiagnosticEntity(nestedRecoveryKey),
                        source: 'detail-field',
                    });
                }
            });
        }, [nestedRecoveryKey, queryClient, queryKey]);
        const recoveryHash = hashKey(effectiveQueryKey);
        const recoveredRef = useRef(null);
        useEffect(() => {
            if (!viewerField || entityId.length === 0)
                return;
            if (queryClient.getQueryData(effectiveQueryKey) !== undefined) {
                recoveredRef.current = null;
                return;
            }
            if (queryClient.getQueryData(queryKey) === undefined)
                return;
            if (recoveredRef.current === recoveryHash)
                return;
            recoveredRef.current = recoveryHash;
            recordViewerOverlayRecovery(entityId, field);
            void refetchRealtimeQuery(queryClient, queryKey);
        }, [viewerField, entityId, field, recoveryHash, queryClient, effectiveQueryKey, queryKey]);
        if (nestedProjection?.field !== field)
            return value;
        if (missingNestedIds.length > 0)
            return undefined;
        const materialized = materializeNestedEntityProjection(queryClient, { [field]: value }, nestedProjection);
        return materialized[field];
    };
}
//# sourceMappingURL=createDetailFieldHook.js.map